import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  IconButton,
  ImageList,
  ImageListItem,
  Grid,
  MenuItem,
} from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiButton from "components/VuiButton";
import VuiTypography from "components/VuiTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useParams } from "react-router-dom";
import Icon from "@mui/material/Icon";

function EngineerPage() {
  const { site_id } = useParams();
  const [maintenanceItems, setMaintenanceItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentItem, setCurrentItem] = useState(null);
  const [taskCompletions, setTaskCompletions] = useState({});
  const [currentPhotos, setCurrentPhotos] = useState([]);
  const [currentComments, setCurrentComments] = useState([]);
  const [currentParts, setCurrentParts] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [editingComment, setEditingComment] = useState(null);
  const [editedCommentText, setEditedCommentText] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [siteName, setSiteName] = useState("");
  const [newPart, setNewPart] = useState({
    part_name: "",
    manufacturer: "",
    part_number: "",
    cost: 0.0,
    quantity: 1,
  });
  const [editingPart, setEditingPart] = useState(null);

  const email = localStorage.getItem("email");
  const userName = localStorage.getItem("name");

  const fetchSiteData = async () => {
    try {
      const response = await fetch(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/site/profile?site_id=${site_id}`
      );
      const data = await response.json();
      setSiteName(data.name);
    } catch (error) {
      console.error("Error fetching site data:", error.message);
    }
  };

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/sites/tasks?site_id=${site_id}`
      );
      if (!response.ok) throw new Error("Failed to fetch tasks");
      const data = await response.json();

      const transformedData = data.map((task) => ({
        id: task.task_id,
        item: task.task_name,
        status: task.status,
        created_at: task.created_at,
        completed_at: task.completed_at,
        photo_count: task.photo_count || 0,
        comment_count: task.comment_count || 0,
        taskitem_count: task.taskitem_count || 0,
        completed_items: task.completed_items || 0,
        part_count: task.part_count || 0,
      }));

      setMaintenanceItems(transformedData);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
    setLoading(false);
  };

  const fetchComments = async (taskId) => {
    try {
      const response = await fetch(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/tasks/comments?task_id=${taskId}`
      );
      if (!response.ok) throw new Error("Failed to fetch comments");
      const data = await response.json();
      setCurrentComments(data);
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  };

  const fetchPhotos = async (taskId) => {
    try {
      const response = await fetch(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/tasks/photos?task_id=${taskId}`
      );
      if (!response.ok) throw new Error("Failed to fetch photos");
      const data = await response.json();
      setCurrentPhotos(data || []);
    } catch (error) {
      console.error("Error fetching photos:", error);
      setCurrentPhotos([]);
    }
  };

  const fetchParts = async (taskId) => {
    try {
      const response = await fetch(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/tasks/parts?task_id=${taskId}`
      );
      if (!response.ok) throw new Error("Failed to fetch parts");
      const data = await response.json();
      setCurrentParts(data || []);
    } catch (error) {
      console.error("Error fetching parts:", error);
      setCurrentParts([]);
    }
  };

  useEffect(() => {
    fetchSiteData();
    fetchTasks();
  }, [site_id]);

  const handleOpenTaskDialog = async (taskId) => {
    try {
      // Fetch task items
      const response = await fetch(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/task-items?task_id=${taskId}`
      );
      if (!response.ok) throw new Error("Failed to fetch task items");
      const taskItems = await response.json();

      // Find the task in maintenanceItems to get other details
      const task = maintenanceItems.find((item) => item.id === taskId);

      // Initialize task completions
      const completions = taskItems.reduce(
        (acc, item) => ({
          ...acc,
          [item.item_id]: item.is_completed,
        }),
        {}
      );

      setCurrentItem({
        ...task,
        id: taskId,
        items: taskItems,
        completionPercentage:
          taskItems.length > 0
            ? (taskItems.filter((item) => item.is_completed).length / taskItems.length) * 100
            : 0,
      });

      setTaskCompletions(completions);

      // Fetch comments, photos, and parts
      await fetchComments(taskId);
      await fetchPhotos(taskId);
      await fetchParts(taskId);
    } catch (error) {
      console.error("Error opening task dialog:", error);
    }
  };

  const handleUploadPhoto = async (event) => {
    const file = event.target.files[0];
    if (!file || !currentItem) return;

    setUploadingPhoto(true);
    try {
      const urlResponse = await fetch(
        "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/tasks/photos/generate-presigned-url",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            task_id: currentItem.id,
            site_id: site_id,
            file_name: file.name,
          }),
        }
      );

      const { upload_url, photo_id, s3_key } = await urlResponse.json();

      const uploadResult = await fetch(upload_url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });

      if (!uploadResult.ok) throw new Error("Upload failed");

      const recordResponse = await fetch(
        "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/tasks/photos/create-photo-record",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            photo_id,
            task_id: currentItem.id,
            s3_key,
            file_name: file.name,
          }),
        }
      );

      const newPhoto = await recordResponse.json();
      setCurrentPhotos((prev) => [newPhoto, ...prev]);
    } catch (error) {
      console.error("Upload failed:", error);
      alert("Upload failed: " + error.message);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      const response = await fetch(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/tasks/photos/delete`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ photo_id: photoId }),
        }
      );

      if (!response.ok) throw new Error("Failed to delete photo");

      // Remove photo from state
      setCurrentPhotos((prev) => prev.filter((photo) => photo.photo_id !== photoId));

      // Update photo count in maintenance items
      if (currentItem) {
        setMaintenanceItems((prevItems) =>
          prevItems.map((item) =>
            item.id === currentItem.id && item.photo_count > 0
              ? { ...item, photo_count: item.photo_count - 1 }
              : item
          )
        );
      }
    } catch (error) {
      console.error("Error deleting photo:", error);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !currentItem) return;

    try {
      const response = await fetch(
        "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/tasks/comments/add",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            task_id: currentItem.id,
            comment_text: newComment,
            created_by: userName || email,
          }),
        }
      );

      const data = await response.json();
      const newCommentWithDetails = {
        ...data,
        comment_text: newComment,
        created_by: userName || email,
        created_at: data.created_at || new Date().toISOString(),
      };

      setCurrentComments([newCommentWithDetails, ...currentComments]);
      setNewComment("");

      // Update comment count
      setMaintenanceItems((prevItems) =>
        prevItems.map((item) =>
          item.id === currentItem.id
            ? { ...item, comment_count: (item.comment_count || 0) + 1 }
            : item
        )
      );
    } catch (error) {
      console.error("Error adding comment:", error);
    }
  };

  const handleEditComment = (comment) => {
    setEditingComment(comment.comment_id);
    setEditedCommentText(comment.comment_text);
  };

  const handleUpdateComment = async (commentId) => {
    try {
      const response = await fetch(
        "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/tasks/comments/update",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            comment_id: commentId,
            comment_text: editedCommentText,
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to update comment");

      setCurrentComments((prev) =>
        prev.map((comment) =>
          comment.comment_id === commentId
            ? { ...comment, comment_text: editedCommentText }
            : comment
        )
      );

      setEditingComment(null);
      setEditedCommentText("");
    } catch (error) {
      console.error("Error updating comment:", error);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await fetch(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/tasks/comments/delete`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ comment_id: commentId }),
        }
      );

      setCurrentComments((prev) => prev.filter((comment) => comment.comment_id !== commentId));

      // Update comment count
      if (currentItem) {
        setMaintenanceItems((prevItems) =>
          prevItems.map((item) =>
            item.id === currentItem.id && item.comment_count > 0
              ? { ...item, comment_count: item.comment_count - 1 }
              : item
          )
        );
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  const handleToggleTaskItemCompletion = async (itemId, isCompleted) => {
    try {
      const response = await fetch(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/task-items/update`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            item_id: itemId,
            is_completed: !isCompleted,
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to update task item");

      // Update local state
      setTaskCompletions((prev) => ({
        ...prev,
        [itemId]: !isCompleted,
      }));

      // Update currentItem completion percentage
      if (currentItem) {
        const updatedItems = currentItem.items.map((item) =>
          item.item_id === itemId ? { ...item, is_completed: !isCompleted } : item
        );

        const completedCount = updatedItems.filter((item) => item.is_completed).length;
        const newPercentage =
          updatedItems.length > 0 ? (completedCount / updatedItems.length) * 100 : 0;

        // Check if all items are completed
        const allItemsCompleted = updatedItems.every((item) => item.is_completed);

        setCurrentItem((prev) => ({
          ...prev,
          items: updatedItems,
          completionPercentage: newPercentage,
        }));

        // If all items are completed, update the task status
        if (allItemsCompleted) {
          const statusResponse = await fetch(
            `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/tasks/update`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                task_id: currentItem.id,
                status: "completed",
              }),
            }
          );

          if (!statusResponse.ok) throw new Error("Failed to update task status");

          // Update the task status in maintenanceItems
          setMaintenanceItems((prevItems) =>
            prevItems.map((item) =>
              item.id === currentItem.id
                ? { ...item, status: "completed", completed_at: new Date().toISOString() }
                : item
            )
          );
        }
      }

      // Update maintenance items count
      setMaintenanceItems((prevItems) =>
        prevItems.map((item) =>
          item.id === currentItem?.id
            ? {
                ...item,
                completed_items: isCompleted
                  ? (item.completed_items || 0) - 1
                  : (item.completed_items || 0) + 1,
              }
            : item
        )
      );
    } catch (error) {
      console.error("Error updating task item:", error);
    }
  };

  const handleAddPart = async () => {
    try {
      if (!currentItem) return;

      const response = await fetch(
        "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/tasks/parts/add",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            task_id: currentItem.id,
            part_name: newPart.part_name,
            manufacturer: newPart.manufacturer,
            part_number: newPart.part_number,
            cost: newPart.cost,
            quantity: newPart.quantity,
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to add part");

      const data = await response.json();
      setCurrentParts([data, ...currentParts]);

      // Reset form
      setNewPart({
        part_name: "",
        manufacturer: "",
        part_number: "",
        cost: 0.0,
        quantity: 1,
      });

      // Update part count
      setMaintenanceItems((prevItems) =>
        prevItems.map((item) =>
          item.id === currentItem.id ? { ...item, part_count: (item.part_count || 0) + 1 } : item
        )
      );
    } catch (error) {
      console.error("Error adding part:", error);
      alert("Failed to add part: " + error.message);
    }
  };

  const handleEditPart = (part) => {
    setEditingPart(part);
  };

  const handleUpdatePart = async () => {
    try {
      if (!editingPart) return;

      const response = await fetch(
        "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/tasks/parts/update",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            part_id: editingPart.part_id,
            part_name: editingPart.part_name,
            manufacturer: editingPart.manufacturer,
            part_number: editingPart.part_number,
            quantity: editingPart.quantity,
            cost: editingPart.cost,
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to update part");

      setCurrentParts((prev) =>
        prev.map((part) => (part.part_id === editingPart.part_id ? editingPart : part))
      );

      setEditingPart(null);
    } catch (error) {
      console.error("Error updating part:", error);
    }
  };

  const handleDeletePart = async (partId) => {
    try {
      await fetch(`https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/tasks/parts/delete`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ part_id: partId }),
      });

      setCurrentParts((prev) => prev.filter((part) => part.part_id !== partId));

      // Update part count
      if (currentItem) {
        setMaintenanceItems((prevItems) =>
          prevItems.map((item) =>
            item.id === currentItem.id && item.part_count > 0
              ? { ...item, part_count: item.part_count - 1 }
              : item
          )
        );
      }
    } catch (error) {
      console.error("Error deleting part:", error);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <Card>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" p={2}>
            <VuiTypography variant="lg" color="white">
              Engineer Tasks - {siteName}
            </VuiTypography>
          </VuiBox>

          {loading ? (
            <VuiBox display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </VuiBox>
          ) : (
            <List>
              {maintenanceItems.map((item) => (
                <ListItem key={item.id}>
                  <VuiBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%" // Make sure it takes the full width
                    flexWrap="wrap" // In case screen is small
                    p={1}
                  >
                    <ListItemText
                      primary={item.item}
                      secondary={item.description}
                      sx={{ "& .MuiTypography-root": { color: "white !important" }, flex: 1 }}
                    />
                    <VuiTypography variant="body2" color="text" mr={2}>
                      {item.taskitem_count
                        ? `${Math.round(
                            (item.completed_items / item.taskitem_count) * 100
                          )}% Complete`
                        : "No Items"}
                    </VuiTypography>
                    <VuiButton color="info" onClick={() => handleOpenTaskDialog(item.id)}>
                      View Task
                    </VuiButton>
                  </VuiBox>
                </ListItem>
              ))}
            </List>
          )}
        </Card>
      </VuiBox>

      {/* Task Dialog */}
      <Dialog
        open={Boolean(currentItem)}
        onClose={() => setCurrentItem(null)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          {currentItem?.item}
          {currentItem?.completionPercentage !== undefined && (
            <VuiTypography variant="body2" color="text">
              {Math.round(currentItem.completionPercentage)}% Complete
            </VuiTypography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Task Items Column */}
            <Grid item xs={12} md={6}>
              <VuiTypography variant="h6" mb={2} style={{ fontSize: "0.875rem" }}>
                Task Checklist
              </VuiTypography>
              <List>
                {currentItem?.items?.length > 0 ? (
                  currentItem.items.map((task) => (
                    <ListItem key={task.item_id}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!taskCompletions[task.item_id]}
                            onChange={() =>
                              handleToggleTaskItemCompletion(
                                task.item_id,
                                taskCompletions[task.item_id]
                              )
                            }
                            color="primary"
                          />
                        }
                        label={
                          <VuiTypography
                            variant="body2"
                            style={{
                              textDecoration: taskCompletions[task.item_id]
                                ? "line-through"
                                : "none",
                              fontSize: "0.875rem", // match the parts section font size
                            }}
                          >
                            {task.item_text}
                          </VuiTypography>
                        }
                      />
                    </ListItem>
                  ))
                ) : (
                  <VuiTypography variant="body2" color="text" style={{ fontSize: "0.875rem" }}>
                    No checklist items for this task.
                  </VuiTypography>
                )}
              </List>

              {/* Parts Section */}
              <VuiTypography variant="h6" mt={4} mb={2} style={{ fontSize: "0.875rem" }}>
                Parts List ({currentParts.length})
              </VuiTypography>
              {currentParts.length > 0 ? (
                <List>
                  {currentParts.map((part) => (
                    <ListItem key={part.part_id}>
                      {editingPart?.part_id === part.part_id ? (
                        <Grid container spacing={2} alignItems="center">
                          {/* Repeat this for all TextFields */}
                          {["Component", "Manufacturer", "Part Number"].map((label, idx) => (
                            <Grid item xs={12} md={4} key={label}>
                              <TextField
                                fullWidth
                                label={label}
                                value={
                                  editingPart[
                                    label === "Component"
                                      ? "part_name"
                                      : label === "Manufacturer"
                                      ? "manufacturer"
                                      : "part_number"
                                  ]
                                }
                                onChange={(e) =>
                                  setEditingPart({
                                    ...editingPart,
                                    [label === "Component"
                                      ? "part_name"
                                      : label === "Manufacturer"
                                      ? "manufacturer"
                                      : "part_number"]: e.target.value,
                                  })
                                }
                                sx={{ 
                  '& .MuiOutlinedInput-root': { width: '100%' }, 
                  '& .MuiInputBase-input': { width: '100% !important' } 
                }}
                                required={label === "Component"}
                                margin="dense"
                                InputLabelProps={{
                                  shrink: true,
                                  style: { fontSize: "0.875rem" },
                                }}
                                InputProps={{
                                  style: { fontSize: "0.875rem", width: '100%' },
                                }}
                              />
                            </Grid>
                          ))}

                          {/* Quantity */}
                          <Grid item xs={12} md={3}>
                            <TextField
                              fullWidth
                              type="number"
                              label="Quantity"
                              value={editingPart.quantity}
                              onChange={(e) =>
                                setEditingPart({ ...editingPart, quantity: e.target.value })
                              }
                              required
                              margin="dense"
                              sx={{ 
                  '& .MuiOutlinedInput-root': { width: '100%' }, 
                  '& .MuiInputBase-input': { width: '100% !important' } 
                }}
                              inputProps={{ min: 0, style: { fontSize: "0.875rem" } }}
                              InputLabelProps={{
                                style: { fontSize: "0.875rem", width: '100%' },
                              }}
                            />
                          </Grid>

                          {/* Cost */}
                          <Grid item xs={12} md={3}>
                            <TextField
                              fullWidth
                              type="number"
                              label="Cost (£)"
                              value={editingPart.cost}
                              onChange={(e) =>
                                setEditingPart({ ...editingPart, cost: e.target.value })
                              }
                              margin="dense"
                              sx={{ 
                  '& .MuiOutlinedInput-root': { width: '100%' }, 
                  '& .MuiInputBase-input': { width: '100% !important' } 
                }}
                              inputProps={{ min: 0, style: { fontSize: "0.875rem", width: '100%' } }}
                              InputLabelProps={{
                                style: { fontSize: "0.875rem" },
                              }}
                            />
                          </Grid>

                          {/* Buttons */}
                          <Grid item xs={12} md={6}>
                            <Box
                              display="flex"
                              flexDirection="column"
                              justifyContent="flex-end"
                              height="100%"
                              gap={1}
                            >
                              <Box display="flex" gap={1}>
                                <Button
                                  fullWidth
                                  variant="contained"
                                  color="primary"
                                  onClick={handleUpdatePart}
                                  startIcon={<Icon>save</Icon>}
                                  sx={{ fontSize: "0.875rem" }}
                                >
                                  Save
                                </Button>
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  color="secondary"
                                  onClick={() => setEditingPart(null)}
                                  startIcon={<Icon>cancel</Icon>}
                                  sx={{ fontSize: "0.875rem" }}
                                >
                                  Cancel
                                </Button>
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                      ) : (
                        <>
                          <ListItemText
                            primary={`${part.part_name} ( ${part.manufacturer} - ${part.part_number})`}
                            secondary={`Quantity: ${part.quantity} - Cost: £${part.cost}`}
                            primaryTypographyProps={{ style: { fontSize: "0.875rem" } }}
                            secondaryTypographyProps={{ style: { fontSize: "0.875rem" } }}
                          />
                          <IconButton
                            onClick={() => handleEditPart(part)}
                            color="primary"
                            size="small"
                            sx={{ mr: 1 }}
                          >
                            <Icon fontSize="small">edit</Icon>
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeletePart(part.part_id)}
                            color="error"
                            size="small"
                          >
                            <Icon fontSize="small">delete</Icon>
                          </IconButton>
                        </>
                      )}
                    </ListItem>
                  ))}
                </List>
              ) : (
                <VuiTypography variant="body2" color="text" style={{ fontSize: "0.875rem" }}>
                  No parts listed for this task.
                </VuiTypography>
              )}

              {/* Add Part Form */}
              <VuiTypography variant="subtitle2" mt={3} mb={1}>
                Add New Part
              </VuiTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Component"
                    value={newPart.part_name}
                    onChange={(e) => setNewPart({ ...newPart, part_name: e.target.value })}
                    margin="dense"
                    sx={{ 
                  '& .MuiOutlinedInput-root': { width: '100%' }, 
                  '& .MuiInputBase-input': { width: '100% !important' } 
                }}
                    InputLabelProps={{
                      shrink: true, // Always keep label in top-left
                      style: { fontSize: "0.875rem" },
                    }}
                    InputProps={{
                      style: { fontSize: "0.875rem", width: '100%' },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Manufacturer"
                    value={newPart.manufacturer}
                    onChange={(e) => setNewPart({ ...newPart, manufacturer: e.target.value })}
                    margin="dense"
                    sx={{ 
                  '& .MuiOutlinedInput-root': { width: '100%' }, 
                  '& .MuiInputBase-input': { width: '100% !important' } 
                }}
                    InputLabelProps={{
                      shrink: true, // Always keep label in top-left

                      style: { fontSize: "0.875rem" },
                    }}
                    InputProps={{
                      style: { fontSize: "0.875rem", width: '100%' },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Part No."
                    value={newPart.part_number}
                    onChange={(e) => setNewPart({ ...newPart, part_number: e.target.value })}
                    margin="dense"
                    sx={{ 
                  '& .MuiOutlinedInput-root': { width: '100%' }, 
                  '& .MuiInputBase-input': { width: '100% !important' } 
                }}
                    InputLabelProps={{
                      style: { fontSize: "0.875rem" },
                      shrink: true, // Always keep label in top-left
                    }}
                    InputProps={{
                      style: { fontSize: "0.875rem", width: '100%' },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Quantity"
                    value={newPart.quantity}
                    onChange={(e) => setNewPart({ ...newPart, quantity: e.target.value })}
                    margin="dense"
                    sx={{ 
                  '& .MuiOutlinedInput-root': { width: '100%' }, 
                  '& .MuiInputBase-input': { width: '100% !important' } 
                }}
                    inputProps={{ min: 0, width: '100%' }}
                    InputLabelProps={{
                      style: { fontSize: "0.875rem" },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Cost (£)"
                    value={newPart.cost}
                    onChange={(e) =>
                      setNewPart({ ...newPart, cost: Math.max(0, Number(e.target.value)) })
                    }
                    margin="dense"
                    sx={{ 
                  '& .MuiOutlinedInput-root': { width: '100%' }, 
                  '& .MuiInputBase-input': { width: '100% !important' } 
                }}
                    inputProps={{ min: 0 , width: '100%'}}
                    InputLabelProps={{
                      style: { fontSize: "0.875rem" },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddPart}
                    fullWidth
                    sx={{ mt: 1 }}
                    disabled={!newPart.part_name}
                  >
                    Add Part
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            {/* Photos and Comments Column */}
            <Grid item xs={12} md={6}>
              <VuiTypography variant="h6" mb={2}>
                Photos ({currentPhotos.length})
              </VuiTypography>
              {currentPhotos.length > 0 ? (
                <ImageList cols={2} gap={8}>
                  {currentPhotos.map((photo) => (
                    <ImageListItem key={photo.photo_id}>
                      <img src={photo.url} alt={photo.file_name} />
                      <IconButton
                        color="error"
                        onClick={() => handleDeletePhoto(photo.photo_id)}
                        sx={{ position: "absolute", top: 5, right: 5 }}
                      >
                        <Icon>delete</Icon>
                      </IconButton>
                    </ImageListItem>
                  ))}
                </ImageList>
              ) : (
                <VuiTypography variant="body2" color="text" style={{ fontSize: "0.875rem" }}>
                  No photos available.
                </VuiTypography>
              )}
              <Button
                variant="contained"
                component="label"
                fullWidth
                color="primary"
                disabled={uploadingPhoto}
                startIcon={<Icon>add_a_photo</Icon>}
                sx={{ mt: 2 }}
              >
                {uploadingPhoto ? "Uploading..." : "Upload Photo"}
                <input
                  type="file"
                  hidden
                  onChange={handleUploadPhoto}
                  accept="image/*"
                  disabled={uploadingPhoto}
                />
              </Button>

              {/* Comments Section */}
              <VuiTypography variant="h6" mt={4} mb={2}>
                Comments ({currentComments.length})
              </VuiTypography>
              <List>
                {currentComments.map((comment) => (
                  <ListItem key={comment.comment_id}>
                    {editingComment === comment.comment_id ? (
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={9}>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            variant="outlined"
                            value={editedCommentText} 
                            onChange={(e) => setEditedCommentText(e.target.value)} 
                            placeholder="Add new comment..."
                            sx={{ mt: 2,'& .MuiOutlinedInput-root': { width: '100%' }, 
                  '& .MuiInputBase-input': { width: '100% !important' }  }}
                            InputProps={{
                              style: { fontSize: "0.875rem", width: '100%'  },
                            }}
                          />
                        </Grid>
                        <Grid item xs={3} display="flex" gap={1} alignItems="center">
                          <IconButton
                            onClick={() => handleUpdateComment(comment.comment_id)}
                            color="primary"
                            size="small"
                          >
                            <Icon>check</Icon>
                          </IconButton>
                          <IconButton
                            onClick={() => setEditingComment(null)}
                            color="secondary"
                            size="small"
                          >
                            <Icon>close</Icon>
                          </IconButton>
                        </Grid>
                      </Grid>
                    ) : (
                      <>
                        <ListItemText
                          primary={
                            <VuiTypography variant="body2" style={{ fontSize: "0.875rem" }}>
                              {comment.comment_text}
                            </VuiTypography>
                          }
                          secondary={
                            <VuiTypography variant="caption" style={{ fontSize: "0.75rem" }}>
                              By {comment.created_by} at{" "}
                              {new Date(comment.created_at).toLocaleString()}
                            </VuiTypography>
                          }
                        />
                        <IconButton
                          onClick={() => handleEditComment(comment)}
                          color="primary"
                          size="small"
                        >
                          <Icon>edit</Icon>
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteComment(comment.comment_id)}
                          color="error"
                          size="small"
                        >
                          <Icon>delete</Icon>
                        </IconButton>
                      </>
                    )}
                  </ListItem>
                ))}
              </List>
              <TextField
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add new comment..."
                sx={{ mt: 2,'& .MuiOutlinedInput-root': { width: '100%' }, 
                  '& .MuiInputBase-input': { width: '100% !important' }  }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddComment}
                fullWidth
                sx={{ mt: 2 }}
                disabled={!newComment.trim()}
              >
                Add Comment
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCurrentItem(null)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}
export default EngineerPage;